/**
 * P2 Shutdown / restart emissive arcs — pure math.
 *
 * Shutdown was a state flip: `mech.shutdown` true → emissive collapses
 * to `EMISSIVE_SHUTDOWN_INTENSITY` instantly. Restart was the inverse
 * snap. Both read as glitches, not events. These utils smooth them:
 *
 *   - shutdownArc: brief blow-out flash (0..flashMs) then exponential
 *     decay to 0 over decayMs.
 *   - restartArc: 0 → base over a longer window with a brief mid-ramp
 *     flicker (boot sequence feel).
 *
 * Both return an `emissiveK` multiplier in [0..PEAK]. The caller maps
 * it into the existing emissive write path so it stacks with heat
 * glow and hit flash.
 */

export const SHUTDOWN_FLASH_MS = 90;
export const SHUTDOWN_DECAY_MS = 320;
export const SHUTDOWN_PEAK_K = 1.6;

export const RESTART_RAMP_MS = 600;
export const RESTART_FLICKER_AT_FRACTION = 0.5;
export const RESTART_FLICKER_DEPTH = 0.4;

/**
 * Multiplier on base emissive during a shutdown sequence.
 * 0..FLASH: ramp 1 → PEAK_K
 * FLASH..FLASH+DECAY: exponential decay PEAK_K → 0
 * After:   0 (caller falls back to the static shutdown intensity)
 */
export function shutdownArc(elapsedMs: number | null): number {
  if (elapsedMs === null || elapsedMs < 0) return 1;
  if (elapsedMs < SHUTDOWN_FLASH_MS) {
    const t = elapsedMs / SHUTDOWN_FLASH_MS;
    return 1 + (SHUTDOWN_PEAK_K - 1) * t;
  }
  if (elapsedMs < SHUTDOWN_FLASH_MS + SHUTDOWN_DECAY_MS) {
    const t = (elapsedMs - SHUTDOWN_FLASH_MS) / SHUTDOWN_DECAY_MS;
    // exp(-3·t) is ~5% at t=1 — visually "decays away".
    return SHUTDOWN_PEAK_K * Math.exp(-3 * t);
  }
  return 0;
}

/**
 * Multiplier on base emissive during a restart boot sequence.
 * 0..RAMP: 0 → 1 with a brief flicker dip at RESTART_FLICKER_AT_FRACTION.
 * After:   1 (caller takes over with full base intensity).
 */
export function restartArc(elapsedMs: number | null): number {
  if (elapsedMs === null || elapsedMs < 0) return 1;
  if (elapsedMs >= RESTART_RAMP_MS) return 1;
  const t = elapsedMs / RESTART_RAMP_MS;
  let v = t; // base ramp
  // Brief flicker dip — sharper than a sin, narrower than a half-period.
  const flickerCenter = RESTART_FLICKER_AT_FRACTION;
  const flickerWidth = 0.08;
  const dist = Math.abs(t - flickerCenter);
  if (dist < flickerWidth) {
    const dip = (1 - dist / flickerWidth) * RESTART_FLICKER_DEPTH;
    v = Math.max(0, v - dip);
  }
  return v;
}
